/**
 * Event Processor - Handles incoming events from the server
 * Applies events to local state with echo prevention and gap detection
 * 
 * Architecture:
 * - Pure domain service (no cross-tab sync)
 * - Processes events and saves to IndexedDB
 * - Does NOT broadcast to other tabs (TabSyncCoordinator handles that)
 * - Does NOT reload memory state (TabSyncCoordinator handles that)
 */

import { localDB, type ScenarioEvent } from '../../db/client'
import type { ItemCreatedEvent, ItemsCreatedEvent } from './types'
import { httpClient } from '../../api/http'
import * as eventHandlers from './handlers'
import { logger } from '../utils/logger'

export interface EventPayload {
  eventType: string
  eventId: string
  scenarioId: string
  sequenceNumber: number
  originId: string
  commandId: string
  serverTimestamp: string
  clientTimestamp: string
  userId?: string
  payload: any
  /** @deprecated entityType is no longer sent by the API */
  entityType?: string
}

export type EventProcessorNotification = 
  | { type: 'snapshot-required'; scenarioId: string }
  | { type: 'access-denied'; scenarioId: string }
  | { type: 'access-restored'; scenarioId: string }
  | { type: 'gap-recovery-start'; scenarioId: string; gapSize: number }
  | { type: 'gap-recovery-progress'; scenarioId: string; current: number; total: number }
  | { type: 'gap-recovery-complete'; scenarioId: string; count: number }
  | { type: 'gap-recovery-failed'; scenarioId: string; error: any }

export interface ProcessEventResult {
  status: 'success' | 'error'
  scenarioId: string
  requiresMemoryReload: boolean
  requiresSnapshot?: boolean
  error?: string
}

class EventProcessor {
  private listeners: Array<(event: ScenarioEvent | EventProcessorNotification) => void> = []
  private processingQueue: Map<string, ScenarioEvent[]> = new Map() // scenarioId -> buffered events
  private accessDeniedScenarios = new Set<string>() // Track scenarios with access denied
  private processingLocks: Map<string, Promise<void>> = new Map() // scenarioId -> processing promise (concurrency control for buffered events)
  private eventProcessingLocks: Map<string, Promise<void>> = new Map() // scenarioId -> processing promise (concurrency control for processEvent)
  
  // Recently processed events cache for deduplication (prevents duplicate SignalR events from being processed)
  private recentlyProcessedEvents = new Map<string, number>() // eventId -> timestamp
  private static RECENT_EVENT_TTL = 5000 // 5 seconds - events processed within this window will be skipped
  private static RECENT_EVENT_CLEANUP_THRESHOLD = 100 // Cleanup when cache reaches this size

  /**
   * Process an incoming event from SignalR (leader tab only)
   * Version-based processing: only apply events with sequenceNumber > scenario.sequenceNumber
   * Uses per-scenario locking to prevent race conditions
   * 
   * Returns result indicating if memory reload is needed (caller's responsibility)
   */
  async processEvent(eventPayload: EventPayload): Promise<ProcessEventResult> {
    const scenarioId = eventPayload.scenarioId
    
    // Wait for any in-progress processing of this scenario
    const existingLock = this.eventProcessingLocks.get(scenarioId)
    if (existingLock) {
      logger.debug(`[EventProcessor] Waiting for in-progress event processing to complete for scenario ${scenarioId}`)
      await existingLock
    }
    
    // Create new lock for this processing operation
    // We need to capture the result but store a void promise in the lock map
    const resultPromise = this._processEventLocked(eventPayload)
    const processingPromise: Promise<void> = resultPromise.then(() => undefined).catch(() => undefined)
    
    this.eventProcessingLocks.set(scenarioId, processingPromise)
    
    try {
      return await resultPromise
    } finally {
      // Clean up lock after processing completes
      if (this.eventProcessingLocks.get(scenarioId) === processingPromise) {
        this.eventProcessingLocks.delete(scenarioId)
      }
    }
  }

  /**
   * Internal implementation of processEvent with lock already acquired
   */
  private async _processEventLocked(eventPayload: EventPayload): Promise<ProcessEventResult> {
    logger.debug('[EventProcessor] Processing event:', eventPayload)
    
    // Check deduplication cache - skip if recently processed (prevents duplicate SignalR/HTTP events)
    if (this.recentlyProcessedEvents.has(eventPayload.eventId)) {
      logger.debug(`[EventProcessor] Skipping recently processed event ${eventPayload.eventId}`)
      return { status: 'success', scenarioId: eventPayload.scenarioId, requiresMemoryReload: false }
    }

    const event = {
      eventId: eventPayload.eventId,
      sequenceNumber: eventPayload.sequenceNumber,
      originId: eventPayload.originId,
      commandId: eventPayload.commandId,
      serverTimestamp: eventPayload.serverTimestamp,
      clientTimestamp: eventPayload.clientTimestamp,
      scenarioId: eventPayload.scenarioId,
      eventType: eventPayload.eventType,
      payload: eventPayload.payload
    } as ScenarioEvent

    // Store event in IndexedDB (always, for replay)
    try {
      await localDB.saveEvent(event)
      // Mark event as recently processed (for deduplication)
      this.markEventProcessed(eventPayload.eventId)
    } catch (error: any) {
      // If duplicate, skip processing (already handled)
      if (error.name === 'ConstraintError' || error.message?.includes('uniqueness')) {
        logger.debug(`[EventProcessor] Event ${event.eventId} seq=${event.sequenceNumber} already exists, skipping`)
        // Also mark as processed since it was already in DB
        this.markEventProcessed(eventPayload.eventId)
        return { status: 'success', scenarioId: event.scenarioId, requiresMemoryReload: false }
      }
      return { status: 'error', scenarioId: eventPayload.scenarioId, requiresMemoryReload: false, error: error.message }
    }

    // Get current scenario to check version
    const scenario = await localDB.getScenario(event.scenarioId)
    
    // Exception: ScenarioCreated event implies the scenario doesn't exist yet
    if (!scenario && event.eventType !== 'ScenarioCreated') {
      logger.warn(`[EventProcessor] Scenario ${event.scenarioId} not found, buffering event`)
      this.bufferEvent(event)
      this.notifyListeners({ type: 'snapshot-required', scenarioId: event.scenarioId })
      return { status: 'success', scenarioId: event.scenarioId, requiresMemoryReload: false, requiresSnapshot: true }
    }

    const currentVersion = scenario?.sequenceNumber ?? -1

    // If scenario has never been synced (sequenceNumber = -1), and this isn't the creation event
    if (currentVersion === -1 && event.eventType !== 'ScenarioCreated') {
      logger.info(`[EventProcessor] Scenario ${event.scenarioId} not synced yet, requesting snapshot`)
      this.notifyListeners({ type: 'snapshot-required', scenarioId: event.scenarioId })
      return { status: 'success', scenarioId: event.scenarioId, requiresMemoryReload: false, requiresSnapshot: true }
    }

    // Discard old events (already applied)
    // Note: First event can have sequence 0 or 1 depending on when scenario was created
    if (event.sequenceNumber < currentVersion) {
      logger.debug(`[EventProcessor] Discarding old event seq=${event.sequenceNumber}, scenario version=${currentVersion}`)
      return { status: 'success', scenarioId: event.scenarioId, requiresMemoryReload: false }
    }

    // Check for gap - if event is more than 1 ahead, trigger gap recovery
    // Special handling for new scenarios: server starts at sequence 1, so if we are at -1, we expect 1
    const expectedSequence = currentVersion === -1 ? 1 : currentVersion + 1
    
    if (event.sequenceNumber > expectedSequence) {
      logger.info(`[EventProcessor] Gap detected: event seq=${event.sequenceNumber}, scenario version=${currentVersion}, expected=${expectedSequence}`)
      this.bufferEvent(event)
      await this.requestMissingEvents(event.scenarioId, currentVersion, event.sequenceNumber)
      // Gap recovery may have processed events, so memory reload needed
      return { status: 'success', scenarioId: event.scenarioId, requiresMemoryReload: true }
    }

    // If we receive an event for a scenario that was access-denied, restore access
    if (this.accessDeniedScenarios.has(event.scenarioId)) {
      logger.info(`[EventProcessor] Access restored for scenario ${event.scenarioId}`)
      this.accessDeniedScenarios.delete(event.scenarioId)
      this.notifyListeners({ type: 'access-restored', scenarioId: event.scenarioId })
    }

    // Apply event to state (sequenceNumber = currentVersion + 1)
    await this.applyEvent(event)

    // Check if we can process buffered events
    await this.processBufferedEvents(event.scenarioId)
    
    // Return success - coordinator will handle memory reload and broadcasting
    return { status: 'success', scenarioId: event.scenarioId, requiresMemoryReload: true }
  }

  /**
   * Buffer event for later processing (gap recovery)
   * Limits buffer size to prevent memory exhaustion
   */
  private bufferEvent(event: ScenarioEvent): void {
    const buffer = this.processingQueue.get(event.scenarioId) || []
    
    // Limit buffer to 500 events per scenario to prevent memory exhaustion
    if (buffer.length >= 500) {
      logger.warn(`[EventProcessor] Buffer full for scenario ${event.scenarioId} (500 events), triggering snapshot recovery`)
      this.notifyListeners({ type: 'snapshot-required', scenarioId: event.scenarioId })
      // Clear buffer since we're going to snapshot anyway
      this.processingQueue.delete(event.scenarioId)
      return
    }
    
    buffer.push(event)
    buffer.sort((a, b) => a.sequenceNumber - b.sequenceNumber)
    this.processingQueue.set(event.scenarioId, buffer)
  }

  /**
   * Request missing events from server (gap recovery)
   */
  private async requestMissingEvents(scenarioId: string, lastSeenSequence: number, currentSequence: number): Promise<void> {
    const gapSize = currentSequence - lastSeenSequence - 1
    logger.debug(`[EventProcessor] Gap detected! Requesting events ${lastSeenSequence + 1} to ${currentSequence - 1} for scenario ${scenarioId} (${gapSize} events)`)
    
    // Show progress notification for large gaps
    if (gapSize > 10) {
      this.notifyListeners({ type: 'gap-recovery-start', scenarioId, gapSize })
    }
    
    try {
      // Use sync endpoint for gap recovery
      const response = await httpClient.syncScenario(scenarioId, lastSeenSequence)

      // Check if snapshot is included
      if (response.snapshot) {
        logger.warn(`[EventProcessor] Snapshot included in sync response for scenario ${scenarioId}, triggering snapshot recovery`)
        this.notifyListeners({ type: 'snapshot-required', scenarioId })
        return
      }

      // Buffer the events for processing (convert from EventPayload to ScenarioEvent format)
      for (const eventPayload of response.events) {
        const event = {
          eventId: eventPayload.eventId,
          sequenceNumber: eventPayload.sequenceNumber,
          originId: eventPayload.originId,
          commandId: eventPayload.commandId,
          serverTimestamp: eventPayload.serverTimestamp,
          clientTimestamp: eventPayload.clientTimestamp,
          scenarioId: eventPayload.scenarioId,
          eventType: eventPayload.eventType,
          payload: eventPayload.payload
        } as ScenarioEvent
        this.bufferEvent(event)
      }

      logger.debug(`[EventProcessor] Recovered ${response.events.length} events for scenario ${scenarioId}`)
      
      // Process buffered events
      await this.processBufferedEvents(scenarioId)
      
      // Notify completion for large gaps
      if (gapSize > 10) {
        this.notifyListeners({ type: 'gap-recovery-complete', scenarioId, count: response.events.length })
      }
      
    } catch (error: any) {
      // Check if it's an authorization error
      if (error.status === 403 || error.status === 401) {
        console.warn(`[EventProcessor] Not authorized to fetch events for scenario ${scenarioId}, ignoring gap`)
        // Clear buffered events for this scenario since we can't recover them
        this.processingQueue.delete(scenarioId)
        // Track this scenario as access-denied
        this.accessDeniedScenarios.add(scenarioId)
        // Notify UI about access denial
        this.notifyListeners({ type: 'access-denied', scenarioId })
        return
      }
      
      console.error(`[EventProcessor] Failed to recover events for scenario ${scenarioId}:`, error)
      
      // Notify failure for large gaps
      if (gapSize > 10) {
        this.notifyListeners({ type: 'gap-recovery-failed', scenarioId, error })
      }
      
      // Re-throw to let caller handle
      throw error
    }
  }

  /**
   * Process buffered events that are now in sequence
   * Uses explicit locking to prevent concurrent processing of the same scenario
   */
  private async processBufferedEvents(scenarioId: string): Promise<void> {
    // Explicit concurrency control: wait for any in-progress processing to complete
    const existingLock = this.processingLocks.get(scenarioId)
    if (existingLock) {
      await existingLock
      // After waiting, check if we still need to process (other call may have done it)
      const buffer = this.processingQueue.get(scenarioId)
      if (!buffer || buffer.length === 0) return
    }
    
    // Create new lock for this processing operation
    const processingPromise = this._processBufferedEventsLocked(scenarioId)
    this.processingLocks.set(scenarioId, processingPromise)
    
    try {
      await processingPromise
    } finally {
      // Clean up lock after processing completes
      if (this.processingLocks.get(scenarioId) === processingPromise) {
        this.processingLocks.delete(scenarioId)
      }
    }
  }
  
  /**
   * Internal implementation of processBufferedEvents with lock already acquired
   */
  private async _processBufferedEventsLocked(scenarioId: string): Promise<void> {
    const buffer = this.processingQueue.get(scenarioId)
    if (!buffer || buffer.length === 0) return

    const scenario = await localDB.getScenario(scenarioId)
    if (!scenario) return

    const currentVersion = scenario.sequenceNumber || 0
    // Special handling for new scenarios: server starts at sequence 1, so if we are at -1 (or 0 implied), we expect 1
    const expectedSequence = (currentVersion === 0 || currentVersion === -1) ? 1 : currentVersion + 1
    const processable: ScenarioEvent[] = []

    logger.debug(`[EventProcessor] Processing buffer for scenario ${scenarioId}, expected seq=${expectedSequence}, buffer has ${buffer.length} events`)

    // Remove events older than or equal to current sequenceNumber (already applied)
    const relevantBuffer = buffer.filter(e => e.sequenceNumber >= expectedSequence)
    if (relevantBuffer.length < buffer.length) {
      logger.debug(`[EventProcessor] Removed ${buffer.length - relevantBuffer.length} old events from buffer`)
    }

    // Deduplicate buffer by sequence number (keep first occurrence)
    const deduplicatedBuffer: ScenarioEvent[] = []
    const seen = new Set<number>()
    for (const event of relevantBuffer) {
      if (!seen.has(event.sequenceNumber)) {
        seen.add(event.sequenceNumber)
        deduplicatedBuffer.push(event)
      }
    }
    if (deduplicatedBuffer.length < relevantBuffer.length) {
      logger.debug(`[EventProcessor] Removed ${relevantBuffer.length - deduplicatedBuffer.length} duplicate events from buffer`)
    }

    // Find consecutive events we can process (in order)
    for (const event of deduplicatedBuffer) {
      const expectedSeq = (currentVersion === 0 || currentVersion === -1) 
        ? 1 + processable.length 
        : currentVersion + processable.length + 1
      
      logger.debug(`[EventProcessor] Checking event seq=${event.sequenceNumber}, expected=${expectedSeq}`)
      if (event.sequenceNumber === expectedSeq) {
        processable.push(event)
      } else {
        break
      }
    }

    logger.debug(`[EventProcessor] Found ${processable.length} processable events`)

    // Process events in batches to avoid blocking UI
    const BATCH_SIZE = 10
    for (let i = 0; i < processable.length; i += BATCH_SIZE) {
      const batch = processable.slice(i, i + BATCH_SIZE)
      
      for (const event of batch) {
        logger.debug(`[EventProcessor] Applying buffered event ${event.eventType} seq=${event.sequenceNumber}`)
        // Save the event to IndexedDB before applying (gap recovery events weren't saved yet)
        try {
          await localDB.saveEvent(event)
        } catch (error: any) {
          // Ignore duplicate constraint errors during gap recovery
          // This can happen if event was saved but scenario update failed previously
          if (error.name !== 'ConstraintError' && !error.message?.includes('uniqueness')) {
            throw error
          }
          logger.debug(`[EventProcessor] Event ${event.eventId} seq=${event.sequenceNumber} already in DB during recovery, continuing application`)
        }
        await this.applyEvent(event)
      }
      
      // TabSyncCoordinator will handle cross-tab notifications
      // (removed eventBroadcaster.notifyScenarioUpdate call as part of Phase 2 refactor)
      
      // Yield to event loop every batch to keep UI responsive
      if (i + BATCH_SIZE < processable.length) {
        await new Promise(resolve => setTimeout(resolve, 0))
      }
    }

    // Remove processed events from buffer
    if (processable.length > 0 || deduplicatedBuffer.length < buffer.length) {
      const remaining = deduplicatedBuffer.slice(processable.length)
      if (remaining.length > 0) {
        this.processingQueue.set(scenarioId, remaining)
      } else {
        this.processingQueue.delete(scenarioId)
      }
    }
  }

  /**
   * Apply event to local state using projection functions
   * Processor orchestrates: opens transaction, calls projection, removes command, updates version, commits
   */
  private async applyEvent(event: ScenarioEvent): Promise<void> {
    logger.debug(`[EventProcessor] Applying event ${event.eventType} seq=${event.sequenceNumber}`, event.payload)

    // Get handler descriptor (includes handler and stores it needs)
    const descriptor = eventHandlers.getHandlerForEvent(event.eventType)

    // Add processor-managed stores: scenarios (for version) and syncQueue (for command removal)
    const allStores = [...new Set([...descriptor.stores, 'scenarios', 'syncQueue'])]

    // Open transaction with all required stores
    const transaction = await localDB.createEventTransactionWithStores(allStores)

    return new Promise((resolve, reject) => {
      transaction.onerror = () => {
        logger.error(`[EventProcessor] Transaction error for event ${event.eventType}:`, transaction.error)
        reject(transaction.error)
      }
      transaction.onabort = () => {
        logger.error(`[EventProcessor] Transaction aborted for event ${event.eventType}`)
        reject(new Error('Transaction aborted'))
      }

      // Execute projection and transaction operations
      const executeProjection = async () => {
        try {
          // Call projection handler (cast to any to work around TypeScript union narrowing limitations)
          await (descriptor.handler as any)(event, transaction)

          // Remove originating command
          await localDB.removeCommandInTransaction(transaction, event.commandId)

          // If the server canonicalized item IDs (merge), rewrite remaining pending commands
          // so they reference the canonical ID instead of the now-invalid local ID.
          const mergedPairs = extractMergedItemIds(event)
          if (mergedPairs.length > 0) {
            await rewritePendingCommands(transaction, event.scenarioId, mergedPairs)
          }

          // Update scenario sequenceNumber
          await localDB.updateScenarioSequenceInTransaction(transaction, event.scenarioId, event.sequenceNumber)

        } catch (error) {
          logger.error(`[EventProcessor] Error in projection for event ${event.eventType}:`, error)
          reject(error)
        }
      }

      // Execute and wait for transaction to complete
      executeProjection().catch(reject)

      transaction.oncomplete = () => {
        logger.debug(`[EventProcessor] Transaction completed for event ${event.eventType} seq=${event.sequenceNumber}`)
        
        // Note: Memory reload is now handled by TabSyncCoordinator
        // This keeps EventProcessor pure (no memory state dependencies)
        
        // Notify listeners AFTER data is persisted (for UI updates)
        this.notifyListeners(event)
        resolve()
      }
    })
  }

  /**
   * Register listener for event updates
   */
  onEvent(callback: (event: ScenarioEvent | EventProcessorNotification) => void): () => void {
    this.listeners.push(callback)
    return () => {
      const index = this.listeners.indexOf(callback)
      if (index > -1) {
        this.listeners.splice(index, 1)
      }
    }
  }

  private notifyListeners(event: ScenarioEvent | EventProcessorNotification): void {
    this.listeners.forEach((listener: any) => {
      try {
        listener(event)
      } catch (error) {
        console.error('[EventProcessor] Error in event listener:', error)
      }
    })
  }

  /**
   * Check for gaps and trigger recovery if needed
   */
  async checkForGaps(scenarioId: string): Promise<void> {
    logger.debug(`[EventProcessor] Checking for gaps in scenario ${scenarioId}`)
    
    try {
      const scenario = await localDB.getScenario(scenarioId)
      if (!scenario || scenario.sequenceNumber === undefined || scenario.sequenceNumber === -1) {
        logger.debug(`[EventProcessor] Scenario ${scenarioId} not loaded, skipping gap check`)
        return
      }

      const currentVersion = scenario.sequenceNumber || 0

      // Skip gap check for newly created scenarios (sequenceNumber 0)
      if (currentVersion === 0) {
        logger.debug(`[EventProcessor] Skipping gap check for newly created scenario ${scenarioId} (version 0)`)
        return
      }

      // Get the latest sequence number from the server using sync
      const response = await httpClient.syncScenario(scenarioId, currentVersion)
      
      if (response.snapshot) {
        // Server returned snapshot (events expired or gap too large)
        logger.debug(`[EventProcessor] Server returned snapshot during polling check for ${scenarioId}`)
        this.notifyListeners({ type: 'snapshot-required', scenarioId })
        return
      }
      
      if (response.events.length > 0) {
        const latestServerSequence = response.events[response.events.length - 1].sequenceNumber
        
        if (latestServerSequence > currentVersion + 1) {
          // Gap detected! Request missing events (already have them from sync response)
          logger.debug(`[EventProcessor] Gap detected: local version=${currentVersion}, server seq=${latestServerSequence}`)
          // Process the events we got from sync
          for (const event of response.events) {
            const scenarioEvent = {
              eventId: event.eventId,
              sequenceNumber: event.sequenceNumber,
              originId: event.originId,
              commandId: event.commandId,
              serverTimestamp: event.serverTimestamp,
              clientTimestamp: event.clientTimestamp,
              scenarioId: event.scenarioId,
              eventType: event.eventType,
              payload: event.payload
            } as ScenarioEvent
            this.bufferEvent(scenarioEvent)
          }
          await this.processBufferedEvents(scenarioId)
        } else if (latestServerSequence === currentVersion + 1 && response.events.length > 0) {
          // Just one new event, process it directly
          logger.debug(`[EventProcessor] One new event detected, processing seq=${latestServerSequence}`)
          const firstEvent = response.events[0]
          await this.processEvent({
            eventType: firstEvent.eventType,
            eventId: firstEvent.eventId,
            scenarioId: firstEvent.scenarioId,
            sequenceNumber: firstEvent.sequenceNumber,
            originId: firstEvent.originId,
            commandId: firstEvent.commandId,
            serverTimestamp: response.events[0].serverTimestamp,
            clientTimestamp: response.events[0].clientTimestamp,
            userId: response.events[0].userId,
            payload: response.events[0].payload
          })
        } else {
          logger.debug(`[EventProcessor] No new events detected`)
        }
      }
    } catch (error) {
      console.error(`[EventProcessor] Error checking for gaps in scenario ${scenarioId}:`, error)
    }
  }

  /**
   * Mark an event as recently processed (for deduplication)
   * Also triggers cleanup if cache is too large
   */
  private markEventProcessed(eventId: string): void {
    this.recentlyProcessedEvents.set(eventId, Date.now())
    
    // Cleanup if cache is getting large
    if (this.recentlyProcessedEvents.size >= EventProcessor.RECENT_EVENT_CLEANUP_THRESHOLD) {
      this.cleanupRecentEvents()
    }
  }

  /**
   * Remove stale entries from the deduplication cache
   */
  private cleanupRecentEvents(): void {
    const now = Date.now()
    const keysToDelete: string[] = []
    
    for (const [eventId, timestamp] of this.recentlyProcessedEvents) {
      if (now - timestamp > EventProcessor.RECENT_EVENT_TTL) {
        keysToDelete.push(eventId)
      }
    }
    
    for (const key of keysToDelete) {
      this.recentlyProcessedEvents.delete(key)
    }
    
    if (keysToDelete.length > 0) {
      logger.debug(`[EventProcessor] Cleaned up ${keysToDelete.length} stale entries from deduplication cache`)
    }
  }

  /**
   * Cleanup - clear internal state
   * Called before HMR reload and in tests
   */
  cleanup(): void {
    // Clear internal buffers and locks (needed for test isolation)
    this.processingQueue.clear()
    this.accessDeniedScenarios.clear()
    this.processingLocks.clear()
    this.eventProcessingLocks.clear()
    this.recentlyProcessedEvents.clear()
  }
}

// ── Merge helpers ──────────────────────────────────────────────────
// When the server canonicalizes an item ID (merge), pending commands in the
// syncQueue may still reference the old (local) ID. These helpers extract
// the (oldId → canonicalId) pairs from events and rewrite any remaining
// pending commands so they target the canonical ID.

interface MergedIdPair { fromItemId: string; toItemId: string }

/**
 * Extract (fromItemId → toItemId) pairs from events that carry mergedFromItemId.
 */
function extractMergedItemIds(event: ScenarioEvent): MergedIdPair[] {
  if (event.eventType === 'ItemCreated') {
    const e = event as ItemCreatedEvent
    if (e.payload.mergedFromItemId && e.payload.mergedFromItemId !== e.payload.itemId) {
      return [{ fromItemId: e.payload.mergedFromItemId, toItemId: e.payload.itemId }]
    }
  }
  if (event.eventType === 'ItemsCreated') {
    const e = event as ItemsCreatedEvent
    const pairs: MergedIdPair[] = []
    for (const item of e.payload.items) {
      if (item.mergedFromItemId && item.mergedFromItemId !== item.itemId) {
        pairs.push({ fromItemId: item.mergedFromItemId, toItemId: item.itemId })
      }
    }
    return pairs
  }
  return []
}

/**
 * Rewrite a single command object, replacing oldItemId references with the canonical ID.
 * Returns a shallow copy if changed, or the original object if unchanged.
 */
function rewriteCommandItemId(command: Record<string, unknown>, fromItemId: string, toItemId: string): { changed: boolean; command: Record<string, unknown> } {
  let changed = false
  let cmd = command

  // Direct itemId field (CompleteItem, RenameItem, DeleteItem, ReorderItem, UpdateItemQuantity, etc.)
  if (cmd.itemId === fromItemId) {
    cmd = { ...cmd, itemId: toItemId }
    changed = true
  }

  // itemIds array (ScenarioItemsDeleted, etc.)
  if (Array.isArray(cmd.itemIds)) {
    const ids = cmd.itemIds as string[]
    const hasOld = ids.includes(fromItemId)
    if (hasOld) {
      cmd = { ...cmd, itemIds: ids.map(id => id === fromItemId ? toItemId : id) }
      changed = true
    }
  }

  // items array (CreateItems — each item has an itemId)
  if (Array.isArray(cmd.items)) {
    const items = cmd.items as Array<Record<string, unknown>>
    let itemsChanged = false
    const mapped = items.map(it => {
      if (it.itemId === fromItemId) {
        itemsChanged = true
        return { ...it, itemId: toItemId }
      }
      return it
    })
    if (itemsChanged) {
      cmd = { ...cmd, items: mapped }
      changed = true
    }
  }

  // Flow rankings keyed by itemId (UpdateFlowRankings)
  if (cmd.commandType === 'UpdateFlowRankings' && cmd.rankings && typeof cmd.rankings === 'object') {
    const rankings = cmd.rankings as Record<string, unknown>
    if (Object.prototype.hasOwnProperty.call(rankings, fromItemId)) {
      const { [fromItemId]: fromRanking, ...rest } = rankings
      // If canonical already has a ranking, keep it; otherwise move the old one over
      const merged = Object.prototype.hasOwnProperty.call(rest, toItemId) ? rest : { ...rest, [toItemId]: fromRanking }
      cmd = { ...cmd, rankings: merged }
      changed = true
    }
  }

  return { changed, command: cmd }
}

/**
 * Walk all pending commands in the syncQueue and rewrite item ID references
 * for the given merged pairs. Runs within an existing transaction.
 */
async function rewritePendingCommands(transaction: IDBTransaction, scenarioId: string, pairs: MergedIdPair[]): Promise<void> {
  const syncStore = transaction.objectStore('syncQueue')

  return new Promise<void>((resolve) => {
    const cursorReq = syncStore.openCursor()
    cursorReq.onsuccess = () => {
      const cursor = cursorReq.result
      if (!cursor) {
        resolve()
        return
      }

      const entry = cursor.value as { command: Record<string, unknown>; [key: string]: unknown }
      const cmd = entry.command
      if (cmd?.scenarioId === scenarioId) {
        let current = cmd
        let anyChanged = false
        for (const { fromItemId, toItemId } of pairs) {
          const result = rewriteCommandItemId(current, fromItemId, toItemId)
          if (result.changed) {
            current = result.command
            anyChanged = true
          }
        }
        if (anyChanged) {
          cursor.update({ ...entry, command: current })
        }
      }
      cursor.continue()
    }
    cursorReq.onerror = () => resolve()
  })
}

// Cleanup previous instance before creating new one (HMR safety)
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    eventProcessor?.cleanup()
  })
}

export const eventProcessor = new EventProcessor()
